import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  Users, Loader2, Phone, Mail, Clock, AlertTriangle, RefreshCw, Inbox,
  ChevronRight, X, MessageSquare, ArrowRight, FileText, Send, Reply,
  FileEdit, Facebook, Copy, CheckCircle2, Zap,
} from "lucide-react";
import { PageHeader } from "../components/ui/kaizen";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { TrialPipelineBoard } from "../components/trial/TrialPipelineBoard";
import { TrialIntegrationPanel } from "../components/trial/TrialIntegrationPanel";

const STAGE_STYLE = {
  nouveau_message: "border-blue-500/50 text-blue-300",
  informations_envoyees: "border-cyan-500/50 text-cyan-300",
  essai_propose: "border-teal-500/50 text-teal-300",
  essai_reserve: "border-violet-500/50 text-violet-300",
  present: "border-emerald-500/50 text-emerald-300",
  absent_reporte: "border-amber-500/50 text-amber-300",
  inscription_en_cours: "border-orange-500/50 text-orange-300",
  membre_actif: "border-green-500/60 text-green-300",
  a_relancer: "border-zinc-500/50 text-zinc-300",
  perdu: "border-red-500/40 text-red-300",
};
const CHANNEL_ICON = (c) => {
  if (/tel|phone/i.test(c)) return "📞";
  if (/facebook|inbox_facebook/i.test(c)) return "📘";
  if (/insta/i.test(c)) return "📸";
  if (/messenger/i.test(c)) return "💬";
  if (/mail|courriel|gmail/i.test(c)) return "✉️";
  if (/formulaire|form/i.test(c)) return "📝";
  if (/qr/i.test(c)) return "🔳";
  if (/essai/i.test(c)) return "🥋";
  return "•";
};
const fmt = (d) => { if (!d) return "—"; try { return new Date(d).toLocaleString("fr-CA", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return d; } };

export default function ProspectCenterModule() {
  const [board, setBoard] = useState({});
  const [counts, setCounts] = useState({});
  const [stages, setStages] = useState([]);
  const [total, setTotal] = useState(0);
  const [overdue, setOverdue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [retention, setRetention] = useState(null);
  const [view, setView] = useState(() => new URLSearchParams(window.location.search).get("vue") === "integration" ? "integration" : "prospects");

  const loadRetention = useCallback(async () => {
    try { const { data } = await api.get("/prospect-center/retention-stats"); setRetention(data); }
    catch { /* non bloquant */ }
  }, []);

  const runTrialReminders = async () => {
    try {
      const { data } = await api.post("/prospect-center/trial-reminders?dry_run=true");
      if (!data.eligible) { toast.info("Aucun essai à relancer pour l'instant (48 h à 7 jours, non finalisé)."); return; }
      if (!window.confirm(`${data.eligible} essai(s) à relancer (courriel + SMS avec lien d'inscription). Envoyer maintenant ?`)) return;
      setBusy(true);
      const res = await api.post("/prospect-center/trial-reminders?dry_run=false");
      toast.success(`✅ ${res.data.sent} relance(s) 48 h envoyée(s)`);
      loadRetention(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur relance 48 h"); }
    setBusy(false);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/prospect-center/board");
      setBoard(data.board || {}); setCounts(data.counts || {}); setStages(data.stages || []);
      setTotal(data.total || 0); setOverdue(data.overdue_actions || 0);
    } catch { toast.error("Erreur chargement du Centre de suivi"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRetention(); }, [loadRetention]);

  const runMigrate = async () => {
    try {
      const { data } = await api.post("/prospect-center/migrate?dry_run=true");
      const msg = `Aperçu : ${data.created_or_updated} fiche(s) à centraliser (${data.crm_leads} CRM, ${data.contact_messages} formulaires, ${data.camille_calls} appels · ${data.skipped_zp_import || 0} alumni ignorés). Lancer la centralisation ?`;
      if (!window.confirm(msg)) return;
      setBusy(true);
      const res = await api.post("/prospect-center/migrate?dry_run=false");
      toast.success(`✅ ${res.data.created_or_updated} fiche(s) centralisée(s)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur migration"); }
    setBusy(false);
  };

  const runGmailIngest = async () => {
    try {
      const { data } = await api.post("/prospect-center/ingest-gmail?dry_run=true");
      if (!data.gmail_connected) { toast.error("Gmail non connecté — reconnecte-le dans Intégrations."); return; }
      if (data.reason) { toast.error(data.reason); return; }
      if (!data.candidates) { toast.info("Aucun nouveau courriel-prospect détecté (30 derniers jours)."); return; }
      if (!window.confirm(`${data.candidates} courriel(s)-prospect détecté(s). Créer/compléter les fiches ?`)) return;
      setBusy(true);
      const res = await api.post("/prospect-center/ingest-gmail?dry_run=false");
      toast.success(`✅ ${res.data.candidates} fiche(s) créée(s)/complétée(s) depuis Gmail`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur ingestion Gmail"); }
    setBusy(false);
  };

  return (
    <div className="p-6 lg:p-10" data-testid="prospect-center-page">
      <PageHeader
        title="Centre de suivi des prospects et inscriptions"
        icon={Users}
        breadcrumbs={[{ label: "Marketing & prospects" }, { label: "Centre de suivi" }]}
        subtitle="Fiche unique par personne · tous canaux (courriel, Facebook, téléphone, formulaire, QR, essai)"
        actions={<>
          <button onClick={() => setShowTemplates(true)} data-testid="pc-templates-btn"
            className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-violet-300 border border-violet-700 px-4 py-2 rounded-lg text-sm">
            <FileEdit className="w-4 h-4" /> Modèles de réponse
          </button>
          <button onClick={runMigrate} disabled={busy} data-testid="pc-migrate-btn"
            className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-cyan-300 border border-cyan-700 px-4 py-2 rounded-lg text-sm disabled:opacity-50">
            <RefreshCw className="w-4 h-4" /> Centraliser les sources
          </button>
          <button onClick={runGmailIngest} disabled={busy} data-testid="pc-ingest-gmail-btn"
            className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-amber-300 border border-amber-700 px-4 py-2 rounded-lg text-sm disabled:opacity-50">
            <Inbox className="w-4 h-4" /> Ingérer les courriels
          </button>
        </>} />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="pc-kpis">
        <Kpi label="Fiches totales" value={total} tone="text-zinc-100" />
        <Kpi label="Nouveaux messages" value={counts.nouveau_message || 0} tone="text-blue-300" />
        <Kpi label="Membres actifs" value={counts.membre_actif || 0} tone="text-green-300" />
        <Kpi label="Actions en retard" value={overdue} tone={overdue ? "text-red-300" : "text-zinc-400"} icon={overdue ? AlertTriangle : Clock} />
      </div>

      {/* Bascule de vue : fiches prospects ↔ pipeline d'intégration des essais */}
      <div className="flex gap-2 mb-5" data-testid="pc-view-toggle">
        <button onClick={() => setView("prospects")} data-testid="pc-view-prospects" className={`px-4 py-2 rounded-lg text-sm font-semibold ${view === "prospects" ? "bg-amber-500 text-black" : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"}`}>Fiches prospects</button>
        <button onClick={() => setView("integration")} data-testid="pc-view-integration" className={`px-4 py-2 rounded-lg text-sm font-semibold ${view === "integration" ? "bg-amber-500 text-black" : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"}`}>🥋 Cours d'essai & intégration</button>
      </div>
      {view === "integration" && <TrialPipelineBoard />}

      {view === "prospects" && <>
      {/* 📈 Rétention — conversion des relances automatiques 48 h */}
      <div className="mb-6 rounded-xl border border-emerald-700/40 bg-emerald-950/15 p-4" data-testid="pc-retention-48h">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-semibold text-emerald-200 flex items-center gap-2">📈 Relances automatiques 48 h — essais → inscriptions</h3>
            <p className="text-xs text-zinc-400 mt-1 max-w-2xl">Après un cours d'essai non finalisé, la plateforme relance automatiquement la famille (courriel + SMS avec lien d'inscription). Voici combien de ces relances se transforment en inscription.</p>
          </div>
          <button onClick={runTrialReminders} disabled={busy} data-testid="pc-run-48h-reminders"
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
            <Send className="w-4 h-4" /> Lancer les relances maintenant
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          <Kpi label="Essais relancés (total)" value={retention?.reminded ?? "—"} tone="text-zinc-100" />
          <Kpi label="Convertis en inscription" value={retention?.converted ?? "—"} tone="text-emerald-300" />
          <Kpi label="Taux de conversion" value={retention ? `${retention.rate}%` : "—"} tone="text-emerald-300" />
          <Kpi label="En attente de décision" value={retention?.pending ?? "—"} tone={retention?.pending ? "text-amber-300" : "text-zinc-400"} />
        </div>
        {retention?.reminded_90d > 0 && (
          <p className="text-[11px] text-zinc-500 mt-2">90 derniers jours : {retention.converted_90d}/{retention.reminded_90d} convertis · {retention.rate_90d}%</p>
        )}
      </div>

      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin text-amber-400 mx-auto mt-10" />
      ) : (
        <>
          {(() => {
            const atRisk = [];
            stages.forEach((s) => (board[s.value] || []).forEach((c) => {
              if (s.value === "membre_actif") return;
              if (c.overdue || /essai/i.test(s.value)) atRisk.push({ ...c, _stage: s.label });
            }));
            if (!atRisk.length) return null;
            return (
              <div className="mb-6 rounded-xl border border-red-700/50 bg-red-950/25 p-4" data-testid="pc-at-risk">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  <span className="font-bold text-red-300">À relancer maintenant · {atRisk.length}</span>
                </div>
                <p className="text-[11px] text-zinc-400 mb-3">Prospects qui ont fait un essai (non inscrits) ou dont l'action de suivi est en retard. Clique une fiche → « Finaliser l'inscription » et envoie-lui le lien.</p>
                <div className="flex flex-wrap gap-2">
                  {atRisk.slice(0, 18).map((c) => {
                    const base = window.location.origin;
                    const q = new URLSearchParams({ source: "suivi" });
                    if (c.email) q.set("courriel", c.email);
                    if (c.name) q.set("nom", c.name);
                    const inscUrl = `${base}/inscription?${q.toString()}`;
                    const prenom = (c.name || "").split(" ")[0] || "";
                    const subject = "Finalise ton inscription — Institut Impact Élite Martial";
                    const body = `Bonjour ${prenom},\n\nMerci d'être venu(e) faire ton cours d'essai ! Pour officialiser ta place et commencer ton parcours, complète ton inscription ici (paiement + contrat, ça prend 2 minutes) :\n\n${inscUrl}\n\nAu plaisir de te revoir sur le tatami,\nL'équipe Institut Impact Élite Martial`;
                    const mailto = `mailto:${c.email || ""}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                    return (
                      <div key={c.id} className="flex items-center gap-1.5 bg-zinc-950 border border-red-800/50 rounded-lg pl-3 pr-1.5 py-1" data-testid={`pc-risk-${c.id}`}>
                        <button onClick={() => setOpenId(c.id)} className="flex items-center gap-1.5 text-xs" data-testid={`pc-risk-open-${c.id}`}>
                          <span className="font-semibold text-zinc-100">{c.name || "(sans nom)"}</span>
                          <span className="text-[10px] text-zinc-500">· {c._stage}</span>
                          {c.overdue && <span className="text-[10px] text-red-400">⏰</span>}
                        </button>
                        {c.email ? (
                          <a href={mailto} data-testid={`pc-risk-relance-${c.id}`}
                            className="text-[10px] bg-emerald-700 hover:bg-emerald-600 text-white font-semibold px-2 py-1 rounded" title="Relancer par courriel (pré-rempli avec le lien d'inscription)">✉️ Relancer</a>
                        ) : (
                          <button onClick={() => setOpenId(c.id)} data-testid={`pc-risk-relance-${c.id}`}
                            className="text-[10px] bg-zinc-800 hover:bg-zinc-700 text-zinc-300 px-2 py-1 rounded" title="Pas de courriel — ouvrir la fiche">✉️ Relancer</button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          <div className="flex gap-3 overflow-x-auto pb-4" data-testid="pc-board">
          {stages.map((s) => {
            const cards = board[s.value] || [];
            return (
              <div key={s.value} data-testid={`pc-col-${s.value}`}
                className={`min-w-[280px] w-[280px] shrink-0 rounded-xl border bg-zinc-900/40 ${STAGE_STYLE[s.value] || "border-zinc-800"}`}>
                <div className="px-3 py-2.5 border-b border-zinc-800 flex items-center justify-between">
                  <span className="text-sm font-bold">{s.label}</span>
                  <span className="text-xs bg-zinc-950 border border-zinc-700 rounded-full px-2 py-0.5">{cards.length}</span>
                </div>
                <div className="p-2 space-y-2 max-h-[64vh] overflow-y-auto">
                  {cards.length === 0 ? (
                    <p className="text-[11px] text-zinc-600 text-center py-4">—</p>
                  ) : cards.map((c) => (
                    <button key={c.id} onClick={() => setOpenId(c.id)} data-testid={`pc-card-${c.id}`}
                      className="w-full text-left rounded-lg bg-zinc-950 border border-zinc-800 hover:border-amber-600/50 p-2.5 transition-colors">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-sm truncate">{c.name || "(sans nom)"}</span>
                        <span title={c.channel_origin} className="text-xs shrink-0">{CHANNEL_ICON(c.channel_origin || "")}</span>
                      </div>
                      <div className="text-[11px] text-zinc-500 truncate">{c.email || c.phone || "—"}</div>
                      {c.program && <div className="text-[10px] text-zinc-600 mt-0.5">{c.program}</div>}
                      {c.next_action && (
                        <div className={`text-[10px] mt-1 flex items-center gap-1 ${c.overdue ? "text-red-400" : "text-amber-400/80"}`}>
                          {c.overdue ? <AlertTriangle className="w-3 h-3" /> : <Clock className="w-3 h-3" />} {c.next_action}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        </>
      )}

      <WebhookCard onIngested={load} />
      </>}

      {openId && <LeadDrawer id={openId} stages={stages} onClose={() => setOpenId(null)} onChanged={load} />}
      {showTemplates && <TemplatesModal onClose={() => setShowTemplates(false)} />}
    </div>
  );
}

function LeadDrawer({ id, stages, onClose, onChanged }) {
  const { user } = useAuth();
  const isReception = !!user && !user.is_admin && (user.role === "reception_prospects" || (user.roles || []).includes("reception_prospects"));
  const [consent, setConsent] = useState({ type: "", channel: "" });
  const [assignTo, setAssignTo] = useState("");
  const assign = async () => { try { await api.post(`/prospect-center/${id}/assign`, { assigned_to: assignTo }); toast.success(assignTo ? `Assignée à ${assignTo}` : "Désassignée"); await load(); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } };
  const [lead, setLead] = useState(null);
  const [note, setNote] = useState("");
  const [savingStage, setSavingStage] = useState(false);
  const [reply, setReply] = useState(null); // {discipline,label,subject,body,to,available_disciplines}
  const [sending, setSending] = useState(false);
  const [sumBusy, setSumBusy] = useState(false);
  const summarize = async () => {
    setSumBusy(true);
    try { const { data } = await api.post(`/prospect-center/${id}/ai-summary`); setLead((l) => ({ ...l, ai_summary: data })); toast.success("Résumé prêt — bon appel !"); }
    catch (e) { toast.error(e.response?.data?.detail || "IA indisponible"); }
    setSumBusy(false);
  };

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/prospect-center/${id}`); setLead(data.lead); }
    catch { toast.error("Erreur chargement fiche"); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const openReply = async (discipline) => {
    try {
      const q = discipline ? `?discipline=${discipline}` : "";
      const { data } = await api.get(`/prospect-center/${id}/reply-preview${q}`);
      const first = (lead?.name || "").split(" ")[0] || "";
      const essai = `${window.location.origin}/cours-essai`;
      setReply({ ...data, kind: "essai", channel: (data.to || lead?.email) ? "email" : "sms",
        phone: lead?.phone || "", sms: `Salut ${first}! Réserve ton cours d'essai (19,98 $) à l'Institut Impact ici: ${essai} 🥋` });
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const sendReply = async () => {
    setSending(true);
    try {
      if (reply.channel === "sms") {
        if (!reply.phone) { toast.error("Numéro de téléphone manquant"); setSending(false); return; }
        if (isReception && !consent.type) { toast.error("Note le consentement (verbal / écrit / formulaire) avant d'envoyer"); setSending(false); return; }
        await api.post(`/prospect-center/${id}/quick-sms`, isReception ? { to: reply.phone, consent: { ...consent, channel: "sms" } } : { to: reply.phone, body: reply.sms });
        toast.success("✅ SMS envoyé");
      } else {
        if (!reply.to || !reply.to.includes("@")) { toast.error("Adresse courriel du prospect manquante"); setSending(false); return; }
        if (isReception && (!consent.type || !consent.channel)) { toast.error("Note le consentement (type + canal) avant d'envoyer le lien"); setSending(false); return; }
        const res = await api.post(`/prospect-center/${id}/quick-reply`, isReception
          ? { to: reply.to, discipline: reply.discipline, consent }
          : { to: reply.to, subject: reply.subject, body: reply.body, discipline: reply.discipline });
        toast.success(`✅ Réponse envoyée (${res.data.channel})`);
      }
      setReply(null); await load(); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'envoi"); }
    setSending(false);
  };

  const changeStage = async (stage) => {
    setSavingStage(true);
    try {
      await api.put(`/prospect-center/${id}/stage`, { stage });
      toast.success("Étape mise à jour");
      await load(); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setSavingStage(false);
  };

  const markMember = async () => {
    if (!window.confirm("Confirmer que ce client a PAYÉ et devient MEMBRE ACTIF ? (à utiliser si le paiement est reçu hors ligne ou déjà finalisé)")) return;
    setSavingStage(true);
    try {
      await api.post(`/prospect-center/${id}/mark-member`);
      toast.success("✅ Marqué « Membre actif »");
      await load(); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setSavingStage(false);
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/prospect-center/${id}/timeline`, { channel: "crm", type: "note", summary: note.trim() });
      setNote(""); await load(); toast.success("Note ajoutée");
    } catch { toast.error("Erreur"); }
  };

  // 📨 Modèles de messages au client (avec les bons liens), envoyables par courriel OU SMS
  const buildTemplates = (l) => {
    const base = window.location.origin;
    const first = (l.name || "").split(" ")[0] || "";
    const q = new URLSearchParams({ source: "suivi" });
    if (l.email) q.set("courriel", l.email);
    if (l.name) q.set("nom", l.name);
    const insc = `${base}/inscription?${q.toString()}`;
    const essai = `${base}/cours-essai`;
    const membre = `${base}/membres`;
    return {
      essai: { label: "Invitation cours d'essai", subject: "Ton cours d'essai t'attend 🥋",
        body: `Bonjour ${first},\n\nMerci de ton intérêt pour l'Institut Impact ! Réserve ton cours d'essai (19,98 $) ici :\n${essai}\n\nAu plaisir de te voir sur le tatami !\n— L'équipe Institut Impact Élite Martial`,
        sms: `Salut ${first}! Réserve ton cours d'essai (19,98 $) à l'Institut Impact ici: ${essai} 🥋` },
      inscription: { label: "🎯 Finaliser l'inscription", subject: "Complète ton inscription (paiement + contrat) 🥋",
        body: `Bonjour ${first},\n\nSuper de t'avoir eu à ton cours d'essai ! Pour officialiser ta place, complète ton inscription (paiement + contrat/waiver) en 2 minutes :\n${insc}\n\nTa page membre et ton lien de connexion sont créés automatiquement ensuite.\n\n— L'équipe Institut Impact Élite Martial`,
        sms: `Salut ${first}! Complète ton inscription (paiement + contrat) ici: ${insc} — ta place t'attend! 🥋` },
      paiement: { label: "Rappel de paiement", subject: "Petit rappel — finaliser ton inscription",
        body: `Bonjour ${first},\n\nIl ne reste qu'une étape pour confirmer ta place : compléter le paiement et le contrat ici :\n${insc}\n\nUne question ? Réponds simplement à ce message.\n\n— L'équipe Institut Impact Élite Martial`,
        sms: `Salut ${first}! Petit rappel: complète ton inscription ici pour confirmer ta place: ${insc}` },
      bienvenue: { label: "Bienvenue membre + lien de connexion", subject: "Bienvenue à l'Institut Impact ! 🥋",
        body: `Bonjour ${first},\n\nBienvenue dans la famille Institut Impact ! 🥋\n\nAccède à ton espace membre et à ton carnet de progression ici :\n${membre}\n\nOn a hâte de te voir progresser !\n— L'équipe Institut Impact Élite Martial`,
        sms: `Bienvenue ${first}! 🥋 Accède à ton espace membre ici: ${membre}` },
      relance: { label: "Relance générale", subject: "On pense à toi 🥋",
        body: `Bonjour ${first},\n\nOn aimerait beaucoup te revoir à l'Institut Impact ! Dis-nous ce qui te ferait plaisir : un cours d'essai, plus d'infos, un horaire ?\n\nRéserve un essai ici :\n${essai}\n\n— L'équipe Institut Impact Élite Martial`,
        sms: `Salut ${first}! On aimerait te revoir à l'Institut Impact 🥋 Réserve un essai ici: ${essai}` },
    };
  };

  const openTemplate = (kind) => {
    const t = buildTemplates(lead)[kind];
    setReply({ kind, label: t.label, subject: t.subject, body: t.body, sms: t.sms,
      to: lead.email || "", phone: lead.phone || "", channel: lead.email ? "email" : "sms",
      available_disciplines: [] });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto bg-zinc-900 border-zinc-700" data-testid="pc-lead-drawer" aria-describedby={undefined}>
        {!lead ? (
          <>
            <DialogHeader>
              <DialogTitle>Fiche prospect</DialogTitle>
              <DialogDescription>Chargement…</DialogDescription>
            </DialogHeader>
            <Loader2 className="w-6 h-6 animate-spin text-amber-400 mx-auto my-10" />
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">{lead.name || "(sans nom)"}</DialogTitle>
              <DialogDescription>{lead.stage_label} · origine : {lead.channel_origin || lead.source || "—"}</DialogDescription>
            </DialogHeader>

            <div className="space-y-1.5 text-sm">
              {lead.email && <div className="flex items-center gap-2 text-zinc-300"><Mail className="w-4 h-4 text-zinc-500" /> <a href={`mailto:${lead.email}`} className="hover:text-amber-300">{lead.email}</a></div>}
              {lead.phone && <div className="flex items-center gap-2 text-zinc-300"><Phone className="w-4 h-4 text-zinc-500" /> <a href={`tel:${lead.phone}`} className="hover:text-amber-300">{lead.phone}</a></div>}
              {lead.program && <div className="text-xs text-zinc-500">Programme : {lead.program}</div>}
              {lead.next_action && <div className="text-xs text-amber-400/90 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Prochaine action : {lead.next_action} {lead.next_action_at ? `(${fmt(lead.next_action_at)})` : ""}</div>}
            </div>

            {/* Réponse en 1 clic */}
            <button onClick={() => openReply()} disabled={!lead.email} data-testid="pc-reply-btn"
              className="mt-3 w-full flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-500 disabled:opacity-40 text-black font-semibold px-4 py-2 rounded-lg text-sm">
              <Reply className="w-4 h-4" /> Répondre (modèle pré-rempli + lien d'essai)
            </button>
            {!lead.email && <p className="text-[11px] text-zinc-600 mt-1 text-center">Aucun courriel sur cette fiche.</p>}

            {/* 📨 Modèles de messages au client (avec liens) — envoi courriel ou SMS */}
            <div className="mt-3 rounded-xl border border-sky-700/40 bg-sky-950/20 p-3" data-testid="pc-templates-section">
              <div className="text-sm font-bold text-sky-300 flex items-center gap-1.5 mb-2"><Send className="w-4 h-4" /> Envoyer un message au client</div>
              <div className="grid grid-cols-2 gap-1.5">
                {[["essai", "🥋 Invitation essai"], ["inscription", "🎯 Finaliser inscription"], ["paiement", "💳 Rappel paiement"], ["bienvenue", "🎉 Bienvenue membre"], ["relance", "💬 Relance générale"]].map(([k, lbl]) => (
                  <button key={k} onClick={() => openTemplate(k)} data-testid={`pc-template-${k}`}
                    className="text-[11px] text-left px-2.5 py-1.5 rounded-lg bg-zinc-800/70 hover:bg-sky-700/50 text-zinc-200 transition">{lbl}</button>
                ))}
              </div>
              <p className="text-[10px] text-zinc-500 mt-1.5">Choisis un modèle → le message et le bon lien se pré-remplissent. Envoie par courriel ou SMS.</p>
            </div>

            {/* 🥋 Évaluation d'intégration (post-essai) — grille coach, statuts, fiche d'intégration */}
            <div className="mt-3" data-testid="pc-integration-section">
              <div className="text-sm font-bold text-amber-300 mb-1.5">🥋 Évaluation d'intégration (après le cours d'essai)</div>
              <TrialIntegrationPanel leadId={id} compact onChanged={onChanged} />
            </div>

            {/* 🎯 Finaliser l'inscription après le cours d'essai */}
            <div className={`mt-3 rounded-xl border p-3 ${/essai/i.test(lead.pipeline_stage || lead.stage_label || "") ? "border-emerald-500 bg-emerald-900/30 ring-2 ring-emerald-500/60" : "border-emerald-700/40 bg-emerald-950/20"}`} data-testid="pc-finalize-section">
              <div className="text-sm font-bold text-emerald-300 flex items-center gap-1.5 mb-1">
                🎯 Finaliser l'inscription
                {/essai/i.test(lead.pipeline_stage || lead.stage_label || "") && (
                  <span className="text-[9px] bg-emerald-500 text-black font-bold px-1.5 py-0.5 rounded-full animate-pulse" data-testid="pc-finalize-priority">⭐ PROCHAINE ÉTAPE — essai fait</span>
                )}
              </div>
              <p className="text-[11px] text-zinc-400 mb-2.5">Après le cours d'essai : envoie au client le lien pour compléter son inscription (paiement + contrat/waiver). Sa page membre est ensuite créée automatiquement avec son lien de connexion.</p>
              {(() => {
                const base = window.location.origin;
                const q = new URLSearchParams({ source: "suivi" });
                if (lead.email) q.set("courriel", lead.email);
                if (lead.name) q.set("nom", lead.name);
                const inscUrl = `${base}/inscription?${q.toString()}`;
                const copy = (url, label) => { navigator.clipboard?.writeText(url); toast.success(`Lien ${label} copié — colle-le au client`); };
                return (
                  <div className="space-y-1.5">
                    <div className="flex gap-1.5">
                      <a href={inscUrl} target="_blank" rel="noreferrer" data-testid="pc-finalize-open"
                        className="flex-1 text-center text-xs bg-emerald-700 hover:bg-emerald-600 text-white font-semibold px-3 py-2 rounded-lg">
                        ➜ Compléter l'inscription (paiement + contrat)
                      </a>
                      <button onClick={() => copy(inscUrl, "d'inscription")} data-testid="pc-finalize-copy"
                        className="text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-200 px-3 py-2 rounded-lg" title="Copier le lien à envoyer au client">📋</button>
                    </div>
                    <div className="grid grid-cols-3 gap-1.5 pt-0.5">
                      <a href="/app/contrats" className="text-center text-[11px] text-zinc-300 bg-zinc-800/60 hover:bg-zinc-700 px-2 py-1.5 rounded" data-testid="pc-finalize-contrat">📝 Contrats</a>
                      <a href="/app/premier-mois" className="text-center text-[11px] text-zinc-300 bg-zinc-800/60 hover:bg-zinc-700 px-2 py-1.5 rounded" data-testid="pc-finalize-equip">🥋 Équipement</a>
                      <a href="/app/activation-membres" className="text-center text-[11px] text-zinc-300 bg-zinc-800/60 hover:bg-zinc-700 px-2 py-1.5 rounded" data-testid="pc-finalize-activation">🔑 Page membre</a>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Convertir en membre en 1 clic */}
            {lead.pipeline_stage !== "membre_actif" && user?.is_admin && (
              <>
                <div className="flex items-center gap-1 text-xs" data-testid="pc-assign">
                  <input value={assignTo} onChange={(e) => setAssignTo(e.target.value)} placeholder={lead?.assigned_to ? `Assignée : ${lead.assigned_to}` : "Assigner à (courriel réception)"} className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-zinc-200 w-56" data-testid="pc-assign-input" />
                  <button onClick={assign} className="px-2 py-1 rounded bg-zinc-800 text-zinc-200" data-testid="pc-assign-btn">👤 Assigner</button>
                </div>
                <button onClick={markMember} disabled={savingStage} data-testid="pc-mark-member-btn"
                  className="mt-2 w-full flex items-center justify-center gap-2 bg-green-700 hover:bg-green-600 disabled:opacity-50 text-white font-semibold px-4 py-2 rounded-lg text-sm">
                  <CheckCircle2 className="w-4 h-4" /> Marquer payé / Membre actif
                </button>
              </>
            )}

            {/* Changer d'étape */}
            <div className="mt-3">
              <div className="text-xs uppercase tracking-wide text-zinc-500 mb-1.5">Étape du pipeline</div>
              <select value={lead.pipeline_stage} disabled={savingStage} onChange={(e) => changeStage(e.target.value)}
                data-testid="pc-stage-select"
                className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-100">
                {stages.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>

            {/* Résumé IA avant appel */}
            <div className="mt-4 rounded-lg border border-violet-500/30 bg-violet-500/[0.05] p-3" data-testid="pc-ai-summary">
              <div className="flex items-center gap-2">
                <div className="text-[10px] tracking-[0.25em] text-violet-300 font-semibold">📞 AVANT D'APPELER — RÉSUMÉ EN 3 LIGNES</div>
                <button onClick={summarize} disabled={sumBusy} className="ml-auto px-3 py-1.5 rounded-md bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white text-xs font-semibold" data-testid="pc-ai-summary-btn">{sumBusy ? "Résumé…" : lead.ai_summary ? "↻ Actualiser" : "✨ Résumer l'historique"}</button>
              </div>
              {lead.ai_summary?.lines ? (
                <ol className="mt-2 space-y-1">
                  {lead.ai_summary.lines.map((ln, i) => <li key={i} className="text-xs text-zinc-200 flex gap-2" data-testid={`pc-ai-summary-line-${i}`}><span className="text-violet-300 font-bold">{i + 1}.</span><span>{ln}</span></li>)}
                  <li className="text-[10px] text-zinc-500 pt-1">Ton suggéré : {lead.ai_summary.tone || "—"} · {lead.ai_summary.events} événement(s) · {String(lead.ai_summary.at || "").slice(0, 16).replace("T", " ")}</li>
                </ol>
              ) : <p className="text-[11px] text-zinc-500 mt-1">Un clic résume tout l'historique (contacts, essai, décision) et suggère l'angle de l'appel.</p>}
            </div>
            {/* Timeline */}
            <div className="mt-4">
              <div className="text-xs uppercase tracking-wide text-zinc-500 mb-2 flex items-center gap-1.5"><MessageSquare className="w-4 h-4" /> Historique</div>
              <div className="space-y-2 max-h-56 overflow-y-auto pr-1" data-testid="pc-timeline">
                {(lead.timeline || []).length === 0 ? (
                  <p className="text-xs text-zinc-600">Aucun événement.</p>
                ) : [...(lead.timeline || [])].reverse().map((ev, i) => (
                  <div key={i} className="rounded-lg bg-zinc-950 border border-zinc-800 px-3 py-2">
                    <div className="flex items-center gap-2 text-[11px] text-zinc-500">
                      <span>{CHANNEL_ICON(ev.channel || "")}</span>
                      <span className="uppercase">{ev.channel}</span>
                      <span>· {fmt(ev.at)}</span>
                    </div>
                    <div className="text-sm text-zinc-200 mt-0.5 whitespace-pre-line">{ev.summary}</div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ajouter une note / appel…"
                  data-testid="pc-note-input"
                  className="flex-1 bg-zinc-950 border border-zinc-700 rounded px-3 py-1.5 text-sm text-zinc-100" />
                <button onClick={addNote} data-testid="pc-note-add" className="bg-amber-600 hover:bg-amber-500 text-black font-semibold px-3 rounded-lg text-sm">Ajouter</button>
              </div>
            </div>

            {reply && (
              <div className="mt-4 rounded-xl border border-amber-700/50 bg-zinc-950 p-3" data-testid="pc-reply-composer">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-bold text-amber-300 flex items-center gap-1.5"><Send className="w-4 h-4" /> Message — {reply.label}</span>
                  <button onClick={() => setReply(null)} className="text-zinc-500 hover:text-zinc-300"><X className="w-4 h-4" /></button>
                </div>
                {/* Canal d'envoi */}
                <div className="flex items-center gap-1 bg-zinc-900 border border-zinc-700 rounded-lg p-0.5 mb-2 w-fit" data-testid="pc-reply-channel">
                  {[["email", "✉️ Courriel"], ["sms", "💬 SMS"]].map(([v, l]) => (
                    <button key={v} onClick={() => setReply({ ...reply, channel: v })} data-testid={`pc-reply-channel-${v}`}
                      className={`px-2.5 py-1 rounded text-[11px] ${reply.channel === v ? "bg-amber-500 text-black font-semibold" : "text-zinc-400 hover:text-zinc-200"}`}>{l}</button>
                  ))}
                </div>
                {reply.channel === "email" && (reply.available_disciplines || []).length > 0 && (
                  <select value={reply.discipline} onChange={(e) => openReply(e.target.value)}
                    data-testid="pc-reply-discipline"
                    className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-xs text-zinc-200 mb-2">
                    {(reply.available_disciplines || []).map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
                  </select>
                )}
                {reply.channel === "email" ? (
                  <>
                    <input value={reply.to} onChange={(e) => setReply({ ...reply, to: e.target.value })}
                      placeholder="courriel du prospect" data-testid="pc-reply-to"
                      className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-xs text-zinc-200 mb-2" />
                    <input value={reply.subject} onChange={(e) => setReply({ ...reply, subject: e.target.value })}
                      data-testid="pc-reply-subject"
                      className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-sm text-zinc-100 mb-2 font-semibold" />
                    <textarea value={reply.body} onChange={(e) => setReply({ ...reply, body: e.target.value })}
                      rows={8} data-testid="pc-reply-body"
                      className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-2 text-sm text-zinc-200 mb-2 whitespace-pre-line" />
                  </>
                ) : (
                  <>
                    <input value={reply.phone || ""} onChange={(e) => setReply({ ...reply, phone: e.target.value })}
                      placeholder="téléphone du prospect" data-testid="pc-reply-phone"
                      className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-xs text-zinc-200 mb-2" />
                    <textarea value={reply.sms || ""} onChange={(e) => setReply({ ...reply, sms: e.target.value })}
                      rows={4} data-testid="pc-reply-sms"
                      className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-2 text-sm text-zinc-200 mb-1 whitespace-pre-line" />
                    <div className="text-[10px] text-zinc-500 mb-2">{(reply.sms || "").length} caractères</div>
                  </>
                )}
                {isReception && (
                  <div className="mb-2 grid grid-cols-2 gap-2 text-xs" data-testid="pc-consent">
                    <select value={consent.type} onChange={(e) => setConsent({ ...consent, type: e.target.value })} className="bg-zinc-900 border border-amber-500/50 rounded px-2 py-1.5 text-zinc-200" data-testid="pc-consent-type"><option value="">Consentement obtenu… *</option><option value="verbal">Verbal (au téléphone)</option><option value="ecrit">Écrit (message)</option><option value="formulaire">Formulaire / QR</option></select>
                    <select value={consent.channel} onChange={(e) => setConsent({ ...consent, channel: e.target.value })} className="bg-zinc-900 border border-amber-500/50 rounded px-2 py-1.5 text-zinc-200" data-testid="pc-consent-channel"><option value="">Canal accepté… *</option><option value="courriel">Courriel</option><option value="sms">SMS</option><option value="les_deux">Courriel + SMS</option></select>
                    <div className="col-span-2 text-[10px] text-amber-300">Rôle Réception : modèle approuvé seulement (texte non modifiable), consentement obligatoire — journalisé.</div>
                  </div>
                )}
                <button onClick={sendReply} disabled={sending} data-testid="pc-reply-send"
                  className="w-full flex items-center justify-center gap-2 bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50 text-white font-semibold px-4 py-2 rounded-lg text-sm">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {reply.channel === "sms" ? "Envoyer le SMS" : "Envoyer le courriel"}
                </button>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TemplatesModal({ onClose }) {
  const [templates, setTemplates] = useState(null);
  const [active, setActive] = useState(0);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/prospect-center/templates/disciplines"); setTemplates(data.templates); }
    catch { toast.error("Erreur chargement modèles"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upd = (i, field, val) => setTemplates((t) => t.map((x, idx) => idx === i ? { ...x, [field]: val } : x));

  const save = async (i) => {
    const t = templates[i];
    setSaving(true);
    try {
      await api.put(`/prospect-center/templates/disciplines/${t.key}`, {
        subject: t.subject, body: t.body_template, sms: t.sms_template || "" });
      toast.success(`Modèle « ${t.label} » enregistré`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto bg-zinc-900 border-zinc-700" data-testid="pc-templates-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileEdit className="w-5 h-5 text-violet-400" /> Modèles de réponse par discipline</DialogTitle>
          <DialogDescription>Personnalise les courriels envoyés via le bouton « Répondre ». Variables : {"{name}"} (prénom) et {"{link}"} (lien d'essai).</DialogDescription>
        </DialogHeader>
        {!templates ? <Loader2 className="w-6 h-6 animate-spin text-amber-400 mx-auto my-10" /> : (
          <>
            <div className="flex gap-1.5 flex-wrap mb-3">
              {templates.map((t, i) => (
                <button key={t.key} onClick={() => setActive(i)} data-testid={`pc-tpl-tab-${t.key}`}
                  className={`text-xs px-3 py-1.5 rounded-full border ${active === i ? "bg-violet-600 border-violet-500 text-white" : "bg-zinc-950 border-zinc-700 text-zinc-400"}`}>
                  {t.label}{t.customized ? " ✎" : ""}
                </button>
              ))}
            </div>
            {templates[active] && (
              <div className="space-y-2" data-testid="pc-tpl-editor">
                <label className="text-xs text-zinc-500">Objet du courriel</label>
                <input value={templates[active].subject} onChange={(e) => upd(active, "subject", e.target.value)}
                  data-testid="pc-tpl-subject"
                  className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-100" />
                <label className="text-xs text-zinc-500">Corps du courriel</label>
                <textarea value={templates[active].body_template} onChange={(e) => upd(active, "body_template", e.target.value)}
                  rows={10} data-testid="pc-tpl-body"
                  className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-200 whitespace-pre-line" />
                <label className="text-xs text-zinc-500">SMS optionnel (Twilio) — laisser vide pour ne pas proposer de SMS</label>
                <input value={templates[active].sms_template || ""} onChange={(e) => upd(active, "sms_template", e.target.value)}
                  data-testid="pc-tpl-sms" placeholder="Ex: Salut {name}, ton cours d'essai (19,98 $) t'attend !"
                  className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-200" />
                <button onClick={() => save(active)} disabled={saving} data-testid="pc-tpl-save"
                  className="mt-2 w-full flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-semibold px-4 py-2 rounded-lg text-sm">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Enregistrer ce modèle
                </button>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function WebhookCard({ onIngested }) {
  const [st, setSt] = useState(null);
  const [testing, setTesting] = useState(false);
  const callbackUrl = `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/meta`;

  useEffect(() => {
    api.get("/webhooks/status").then(({ data }) => setSt(data)).catch(() => {});
  }, []);

  const copy = (txt, label) => { navigator.clipboard?.writeText(txt); toast.success(`${label} copié`); };

  const testIngest = async () => {
    setTesting(true);
    try {
      await api.post("/webhooks/test-ingest", { platform: "facebook", external_id: `test-${Date.now()}`,
        name: "Prospect Test FB", text: "Bonjour, je veux des infos sur les cours de kickboxing !" });
      toast.success("✅ Message test ingéré — une fiche a été créée");
      onIngested && onIngested();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setTesting(false);
  };

  if (!st) return null;
  return (
    <div className="mt-8 rounded-xl border border-blue-800/40 bg-blue-950/10 p-5" data-testid="pc-webhook-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-bold text-blue-200 flex items-center gap-2"><Facebook className="w-5 h-5" /> Ingestion Facebook / Instagram en temps réel</h3>
        <span className={`text-xs px-2.5 py-1 rounded-full border ${st.ready ? "bg-emerald-900/40 border-emerald-700 text-emerald-300" : "bg-amber-900/40 border-amber-700 text-amber-300"}`}>
          {st.ready ? "✓ Prêt côté app" : "⚠ À configurer"}
        </span>
      </div>
      <p className="text-xs text-zinc-400 mt-1.5 leading-relaxed">
        Copie ces 2 valeurs dans <b>developer.facebook.com → ton App → Webhooks</b> pour que chaque message FB/IG crée automatiquement une fiche ici.
      </p>
      <div className="grid md:grid-cols-2 gap-3 mt-3">
        <Field label="Callback URL" value={callbackUrl} onCopy={() => copy(callbackUrl, "URL")} testid="pc-webhook-callback" />
        <Field label="Verify Token" value={st.verify_token || "—"} onCopy={() => copy(st.verify_token || "", "Token")} testid="pc-webhook-token" />
      </div>
      <ol className="text-[11px] text-zinc-500 mt-3 space-y-0.5 list-decimal pl-4">
        {(st.instructions || []).map((s, i) => {
          const clean = s.replace(/^\d+\.\s*/, "").replace(st.callback_url || "___none___", callbackUrl);
          return <li key={i}>{clean}</li>;
        })}
      </ol>
      <button onClick={testIngest} disabled={testing} data-testid="pc-webhook-test"
        className="mt-3 flex items-center gap-2 bg-blue-700 hover:bg-blue-600 disabled:opacity-50 text-white font-semibold px-4 py-2 rounded-lg text-sm">
        {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Tester l'ingestion (crée une fiche démo)
      </button>
    </div>
  );
}

const Field = ({ label, value, onCopy, testid }) => (
  <div className="rounded-lg bg-zinc-950 border border-zinc-800 px-3 py-2">
    <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-0.5">{label}</div>
    <div className="flex items-center gap-2">
      <code className="text-xs text-zinc-200 truncate flex-1" data-testid={testid}>{value}</code>
      <button onClick={onCopy} className="text-zinc-400 hover:text-white shrink-0"><Copy className="w-3.5 h-3.5" /></button>
    </div>
  </div>
);

const Kpi = ({ label, value, tone, icon: Icon }) => (
  <div className="rounded-xl bg-zinc-900/60 border border-zinc-800 px-4 py-3">
    <div className={`text-2xl font-bold flex items-center gap-1.5 ${tone || "text-zinc-100"}`}>
      {Icon && <Icon className="w-5 h-5" />}{value}
    </div>
    <div className="text-[11px] text-zinc-500 mt-0.5">{label}</div>
  </div>
);
